import React, { useState, useRef, useEffect } from 'react';
import { Download, ChevronDown } from 'lucide-react';
import { PolymerGroup, POLYMER_GRADES } from '@/constants/polymerGrades';
import FairPriceGradeSelectionModal from '@/components/FairPriceGradeSelectionModal';
import { generateExportFilename, getReadableLabel } from '@/utils/fairPriceExportUtils';
import { useAuth } from '@/contexts/AuthContext';

interface FairPriceExportButtonProps {
  polymer: PolymerGroup;
  rawFairPriceData: any[];
  disabled?: boolean;
}

const FairPriceExportButton: React.FC<FairPriceExportButtonProps> = ({
  polymer,
  rawFairPriceData,
  disabled = false,
}) => {
  const { user } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const grades = POLYMER_GRADES[polymer] || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showDropdown]);

  /**
   * Generate Excel file with selected grades
   */
  const generateExcelFile = async (selectedGrades: string[]) => {
    if (!rawFairPriceData || rawFairPriceData.length === 0) {
      alert('No data available to export');
      return;
    }

    setIsGenerating(true);
    try {
      // Lazy-load ExcelJS to avoid bundle bloat
      const ExcelJS = await import('exceljs');
      const Workbook = ExcelJS.default.Workbook;

      // Parse and transform data
      let exportData = rawFairPriceData
        .map((d) => {
          const dateStr = String(d.Date || d.date || '').slice(0, 10);
          return { ...d, __dateStr: dateStr };
        })
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d.__dateStr));

      // Sort by date descending (newest first)
      exportData.sort((a, b) =>
        a.__dateStr > b.__dateStr ? -1 : a.__dateStr < b.__dateStr ? 1 : 0
      );

      // Create workbook
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Fair Price Data');

      // Add headers: Date + selected grades with readable labels
      const headers = [
        'Date',
        ...selectedGrades.map((grade) => getReadableLabel(grade)),
      ];
      worksheet.addRow(headers);

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1a1a1a' },
      };

      // Add data rows
      exportData.forEach((row) => {
        const dataRow = [
          row.__dateStr,
          ...selectedGrades.map((grade) => {
            const value = row[grade];
            return value !== null && value !== undefined ? value : '';
          }),
        ];
        worksheet.addRow(dataRow);
      });

      // Format columns
      worksheet.columns = [
        { header: 'Date', key: 'Date', width: 12 },
        ...selectedGrades.map((grade) => ({
          header: getReadableLabel(grade),
          key: grade,
          width: 15,
        })),
      ];

      try {
        if (user?.email) {
          worksheet.headerFooter = { oddFooter: `&L&6&KFDFDFD ${user.email}` };
        }
      } catch {}

      // Generate file and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = generateExportFilename(polymer);
      link.click();
      URL.revokeObjectURL(url);

      setShowModal(false);
      setShowDropdown(false);
    } catch (error) {
      console.error('Error generating Excel file:', error);
      alert('Failed to generate Excel file. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Handle "Download All" option
   */
  const handleDownloadAll = async () => {
    await generateExcelFile(grades.map((g) => g.key));
  };

  /**
   * Handle "Select to Download" option
   */
  const handleSelectToDownload = () => {
    setShowDropdown(false);
    setShowModal(true);
  };

  /**
   * Handle export from modal
   */
  const handleExportFromModal = async (selectedGrades: string[]) => {
    await generateExcelFile(selectedGrades);
  };

  const isDisabled = disabled || rawFairPriceData.length === 0 || isGenerating;

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={isDisabled}
          className="flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium bg-rs-green text-black hover:bg-rs-green/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-rs-green"
          title="Export fair price data"
        >
          <Download size={16} />
          Export
          <ChevronDown size={16} className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown Menu */}
        {showDropdown && !isDisabled && (
          <div className="absolute top-full right-0 mt-1 bg-rs-jet border border-rs-jet-light rounded-lg shadow-xl z-40 min-w-max">
            <button
              onClick={handleDownloadAll}
              disabled={isGenerating}
              className="w-full text-left px-4 py-2.5 text-rs-off-white hover:bg-rs-jet-light disabled:opacity-50 transition-colors text-sm font-medium first:rounded-t-lg"
            >
              Download All
            </button>
            <div className="border-t border-rs-jet-light" />
            <button
              onClick={handleSelectToDownload}
              disabled={isGenerating}
              className="w-full text-left px-4 py-2.5 text-rs-off-white hover:bg-rs-jet-light disabled:opacity-50 transition-colors text-sm font-medium last:rounded-b-lg"
            >
              Select to Download...
            </button>
          </div>
        )}
      </div>

      {/* Grade Selection Modal */}
      {showModal && (
        <FairPriceGradeSelectionModal
          polymer={polymer}
          grades={grades}
          onExport={handleExportFromModal}
          onCancel={() => setShowModal(false)}
          isGenerating={isGenerating}
        />
      )}
    </>
  );
};

export default FairPriceExportButton;
